/**
 * CogsJournalsCard — daily COGS Manual Journals (DR COGS / CR Inventory) for
 * every unit consumed by fulfilled orders. Lifted out of the original
 * XeroAccountingCards so it can be the body of the COGS tab on /xero.
 *
 * All Buttons keep type="button" so the card stays safe if ever re-mounted
 * inside a <form>. window.confirm is never used — the enable flow is gated by
 * an AlertDialog (DESIGN.md §10).
 */
import { useEffect, useState } from 'react';
import { BookOpenCheck, ChevronDown, Loader2, Play } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { cn } from '@/lib/utils';
import {
  AccountCodeSelect,
  JournalLink,
  JournalStatusBadge,
  StatTile,
} from './shared';
import { useDropdownAccounts } from '../lib/useDropdownAccounts';
import { formatDateTime } from '../lib/format';

interface CogsJournalsCardProps {
  /** Enable, account config, and Process now are adminProcedure on the backend. */
  isAdmin: boolean;
}

export function CogsJournalsCard({ isAdmin }: CogsJournalsCardProps) {
  const utils = trpc.useUtils();
  const { format } = useCurrencyFormatter();
  const accounts = useDropdownAccounts(true);

  const settingsQuery = trpc.xero.getCogsSettings.useQuery();
  const statsQuery = trpc.xero.getCogsStats.useQuery();
  const logQuery = trpc.xero.getCogsLog.useQuery({ limit: 20 });

  const settings = settingsQuery.data;
  const stats = statsQuery.data;

  const [draftCogsCode, setDraftCogsCode] = useState('');
  const [draftInventoryCode, setDraftInventoryCode] = useState('');
  const [draftStartDate, setDraftStartDate] = useState('');
  const [configOpen, setConfigOpen] = useState(false);
  const [confirmEnableOpen, setConfirmEnableOpen] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setDraftCogsCode(settings.cogsAccountCode);
    setDraftInventoryCode(settings.cogsInventoryAccountCode);
    setDraftStartDate(settings.cogsStartDate ?? '');
  }, [settings]);

  const configDirty =
    settings !== undefined &&
    (draftCogsCode !== settings.cogsAccountCode ||
      draftInventoryCode !== settings.cogsInventoryAccountCode ||
      draftStartDate !== (settings.cogsStartDate ?? ''));

  const invalidateAll = () =>
    Promise.all([
      utils.xero.getCogsSettings.invalidate(),
      utils.xero.getCogsStats.invalidate(),
      utils.xero.getCogsLog.invalidate(),
    ]);

  const updateSettings = trpc.xero.updateCogsSettings.useMutation({
    onSuccess: async () => {
      await invalidateAll();
      toast.success('COGS settings saved');
    },
    onError: (error) => toast.error(error.message || 'Failed to save COGS settings'),
  });

  const processCogs = trpc.xero.processCogs.useMutation({
    onSuccess: async (result) => {
      await invalidateAll();
      if (result.skipped) {
        toast.info('COGS journals are disabled, nothing was processed.');
      } else if (result.failed > 0) {
        toast.error(
          `Processed ${result.processed} with ${result.failed} failures. Check the push log below.`
        );
      } else if (result.processed === 0) {
        toast.success('Nothing pending to journal.');
      } else {
        toast.success(
          `${result.processed} transaction${result.processed === 1 ? '' : 's'} journaled (${format(result.totalAmount, 'AUD')}).`
        );
      }
    },
    onError: (error) => toast.error(error.message || 'Failed to process COGS journals'),
  });

  const enabled = settings?.cogsEnabled ?? false;

  const handleToggle = (next: boolean) => {
    if (next) {
      setConfirmEnableOpen(true);
    } else {
      updateSettings.mutate({ cogsEnabled: false });
    }
  };

  const cogsAccountLabel = (code: string) => {
    const match = accounts.find((account) => account.account_code === code);
    return match ? `${match.account_code} ${match.name}` : code;
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
              <BookOpenCheck className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
            </div>
            <div>
              <CardTitle className="text-base">COGS journals</CardTitle>
              <CardDescription>
                Posts a daily Manual Journal to Xero for every unit consumed by fulfilled orders.
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <span className="text-xs text-muted-foreground" id="cogs-enabled-label">
              {enabled ? 'On' : 'Off'}
            </span>
            <Switch
              checked={enabled}
              onCheckedChange={handleToggle}
              disabled={settingsQuery.isLoading || updateSettings.isPending || !isAdmin}
              aria-labelledby="cogs-enabled-label"
              title={isAdmin ? undefined : 'Enabling COGS journals requires Admin access'}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Stats strip */}
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          <StatTile
            label="Pending"
            value={stats ? String(stats.pendingCount) : '-'}
            foot={
              stats
                ? `${format(stats.pendingAmount, 'AUD')}${stats.pendingLimitReached ? ' (first batch)' : ''}`
                : undefined
            }
            tone={stats && stats.pendingCount > 0 ? 'amber' : 'neutral'}
          />
          <StatTile
            label="Pushed"
            value={stats ? String(stats.totalPushed) : '-'}
            tone={stats && stats.totalPushed > 0 ? 'emerald' : 'neutral'}
          />
          <StatTile
            label="Failed"
            value={stats ? String(stats.totalFailed) : '-'}
            tone={stats && stats.totalFailed > 0 ? 'amber' : 'neutral'}
          />
          <StatTile label="Last push" value={stats ? formatDateTime(stats.lastPushAt) : '-'} />
        </div>

        {/* Collapsible account configuration */}
        <Collapsible open={configOpen} onOpenChange={setConfigOpen}>
          <CollapsibleTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 w-full justify-between px-2 text-xs"
            >
              <span>
                Account configuration
                {settings && (
                  <span className="ml-2 font-normal text-muted-foreground">
                    DR {settings.cogsAccountCode} / CR {settings.cogsInventoryAccountCode}
                    {settings.cogsStartDate ? ` · from ${settings.cogsStartDate}` : ''}
                  </span>
                )}
              </span>
              <ChevronDown
                className={cn(
                  'h-3.5 w-3.5 transition-transform motion-reduce:transition-none',
                  configOpen && 'rotate-180'
                )}
                aria-hidden="true"
              />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-3 pt-3">
            <div className="grid gap-3 md:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="cogs-account">COGS account</Label>
                <AccountCodeSelect
                  id="cogs-account"
                  value={draftCogsCode}
                  onChange={setDraftCogsCode}
                  accounts={accounts}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cogs-inventory-account">Inventory account</Label>
                <AccountCodeSelect
                  id="cogs-inventory-account"
                  value={draftInventoryCode}
                  onChange={setDraftInventoryCode}
                  accounts={accounts}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cogs-start-date">Start date</Label>
                <Input
                  id="cogs-start-date"
                  type="date"
                  className="h-8 text-sm"
                  value={draftStartDate}
                  onChange={(event) => setDraftStartDate(event.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Transactions before this Melbourne day are never journaled.
                </p>
              </div>
            </div>
            {configDirty && (
              <Button
                type="button"
                size="sm"
                className="h-8 px-3 text-xs gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
                disabled={updateSettings.isPending || !draftCogsCode || !draftInventoryCode || !isAdmin}
                onClick={() =>
                  updateSettings.mutate({
                    cogsAccountCode: draftCogsCode,
                    cogsInventoryAccountCode: draftInventoryCode,
                    cogsStartDate: draftStartDate || null,
                  })
                }
              >
                {updateSettings.isPending && (
                  <Loader2
                    className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none"
                    aria-hidden="true"
                  />
                )}
                Save accounts
              </Button>
            )}
          </CollapsibleContent>
        </Collapsible>

        {/* Process now */}
        <div className="flex items-center justify-between gap-3 border-t pt-3">
          <p className="text-xs text-muted-foreground">
            Runs daily at 3:00 AM Melbourne time when enabled. Process manually any time.
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 shrink-0 px-3 text-xs gap-1.5"
            onClick={() => processCogs.mutate(undefined)}
            disabled={!enabled || processCogs.isPending || !isAdmin}
          >
            {processCogs.isPending ? (
              <Loader2
                className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none"
                aria-hidden="true"
              />
            ) : (
              <Play className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            {processCogs.isPending ? 'Pushing journals' : 'Process now'}
          </Button>
        </div>

        {/* Recent push log */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Recent pushes</h4>
          {logQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading push log…</p>
          ) : (logQuery.data?.data.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">No journals pushed yet.</p>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <Table className="min-w-[720px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="px-3 py-1.5">Pushed</TableHead>
                    <TableHead className="px-3 py-1.5">Journal</TableHead>
                    <TableHead className="px-3 py-1.5">SKU</TableHead>
                    <TableHead className="px-3 py-1.5 text-right">Qty</TableHead>
                    <TableHead className="px-3 py-1.5 text-right">Amount</TableHead>
                    <TableHead className="px-3 py-1.5">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(logQuery.data?.data ?? []).map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="px-3 py-1.5 text-xs text-muted-foreground whitespace-nowrap">
                        {formatDateTime(row.pushed_at)}
                      </TableCell>
                      <TableCell className="px-3 py-1.5 text-xs">
                        <JournalLink
                          journalId={row.xero_journal_id}
                          journalNumber={row.journal_number}
                        />
                      </TableCell>
                      <TableCell className="px-3 py-1.5 font-mono text-xs">
                        {row.sku ?? '-'}
                      </TableCell>
                      <TableCell className="px-3 py-1.5 text-right text-xs tabular-nums">
                        {row.quantity_change ?? '-'}
                      </TableCell>
                      <TableCell className="px-3 py-1.5 text-right text-xs tabular-nums">
                        {format(Number(row.amount), 'AUD')}
                      </TableCell>
                      <TableCell className="px-3 py-1.5">
                        <JournalStatusBadge status={row.status} />
                        {row.status === 'failed' && row.error_message && (
                          <p
                            className="mt-0.5 max-w-[220px] truncate text-[11px] text-destructive"
                            title={row.error_message}
                          >
                            {row.error_message}
                          </p>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </CardContent>

      {/* Enable confirmation */}
      <AlertDialog open={confirmEnableOpen} onOpenChange={setConfirmEnableOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Turn on automatic COGS journals?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  Each day at 3:00 AM Melbourne time, the IMS will create Manual Journals in Xero
                  for every unit consumed by sales and consumables.
                </p>
                <p>These journals will post to:</p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>DR {cogsAccountLabel(draftCogsCode || '330')} (Cost of Goods Sold)</li>
                  <li>CR {cogsAccountLabel(draftInventoryCode || '631')} (Inventory Control)</li>
                </ul>
                <p>
                  Confirm these account codes with your accountant before enabling. You can pause
                  auto-push at any time.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                updateSettings.mutate({
                  cogsEnabled: true,
                  cogsAccountCode: draftCogsCode || undefined,
                  cogsInventoryAccountCode: draftInventoryCode || undefined,
                  cogsStartDate: draftStartDate || null,
                })
              }
            >
              Turn on COGS auto-push
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
